import { queryKeys } from '@query/keys'
import { useQuery } from '@tanstack/react-query'

import { useApiClient } from '@utils/ApiClient'
import { ClientApiClient } from '@utils/clientApi'
import { useDebouncedValue } from '@utils/debounce'

const PHONE_NUMBER_VALIDATION_DEBOUNCE_MS = 1000
const MIN_PHONE_NUMBER_LENGTH = 4

interface UseClientPhoneNumberValidationOptions {
  countryCode: string
  phoneNumber: string
  enabled: boolean
}

export const useClientPhoneNumberValidation = ({
  countryCode,
  phoneNumber,
  enabled,
}: UseClientPhoneNumberValidationOptions) => {
  const apiClient = useApiClient(ClientApiClient)
  const canValidate = enabled && phoneNumber.length >= MIN_PHONE_NUMBER_LENGTH
  const fullPhoneNumber = `${countryCode}${phoneNumber}`
  const phoneNumberToValidate = canValidate ? fullPhoneNumber : ''
  const debouncedPhoneNumber = useDebouncedValue(
    phoneNumberToValidate,
    PHONE_NUMBER_VALIDATION_DEBOUNCE_MS
  )
  const isPendingValidation = debouncedPhoneNumber !== phoneNumberToValidate

  const { isSuccess, isError } = useQuery({
    queryKey: queryKeys.profile.phoneNumberValidation(debouncedPhoneNumber),
    queryFn: async () => {
      await apiClient.validateClientPhoneNumber(debouncedPhoneNumber)
      return true
    },
    enabled: canValidate && !isPendingValidation && !!debouncedPhoneNumber,
    retry: false,
  })

  return {
    isValid: canValidate && !isPendingValidation && isSuccess,
    isInvalid: canValidate && !isPendingValidation && isError,
  }
}
